import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Fieldset,
  GDSPopoverPrimitive,
  Popover,
  Radio,
  StackLayout,
  styled,
} from '@leagueplatform/genesis-core';
import {
  type MasonryEngineNodeAction,
  type MasonryEngineActionHandler,
  useMasonryEngineActionEmitter,
} from '@leagueplatform/masonry-engine';
import { type MasonryDriverAction } from '@leagueplatform/masonry-renderers';
import {
  replaceSearchParam,
  FILTERS_CHANGED_EVENT,
} from 'utils/replace-search-param.util';

// SCFHP override for stock `selectInput`: a Claims-style dropdown button that
// opens a radio-option popover (Figma) instead of a native select.

export const RESOLVER_PARAM_BY_FIELD: Record<string, string> = {
  statusCode: 'statusCodes',
  formType: 'formTypes',
  submissionDate: 'submissionDateFrom',
};

const SENTINEL_VALUES_FOR_ALL = new Set(['', 'ALL']);
export const RELOAD_NODE_ID = 'formStatuses$rootWebPaginationLayout';
// Reloaded alongside the list when a chip/Clear All changes filters, so the
// dropdowns re-render with the cleared selection (they read `value` from the
// resolver, which merges the query params).
export const FILTER_BAR_NODE_ID = 'formStatuses$rootFiltersBar';

export const FILTER_NAMESPACE = 'scfhp_form_status_filter';

type SetFilterAction = MasonryEngineNodeAction<
  'setFilter',
  {
    nodeId: string;
    paramKey: string;
    paramValue: string;
  }
>;

export const getSetFilterAction = (
  nodeId: string,
  paramKey: string,
  paramValue: string,
): SetFilterAction => ({
  type: 'setFilter',
  namespace: FILTER_NAMESPACE,
  payload: { nodeId, paramKey, paramValue },
});

// Mirrors paginationLayout's setPageActionHandler — calls
// messagingController.sendMessage so `setQueryParams` actually flows
// into the masonry-node fetch URL on reload.
export const setFilterActionHandler: MasonryEngineActionHandler<
  MasonryEngineNodeAction<'setFilter', Record<string, any>>,
  MasonryDriverAction
> = ({ payload }, { messagingController }) => {
  messagingController.sendMessage(payload.nodeId as string, {
    type: 'reload',
    payload: {
      renderIntermediately: 'loader',
      setQueryParams: (current: Record<string, string>) => {
        const next = { ...current };
        if (payload.paramValue === '') {
          delete next[payload.paramKey as string];
        } else {
          next[payload.paramKey as string] = payload.paramValue as string;
        }
        // Reset to page 1 so the user doesn't land on an out-of-range
        // page after the filtered result count shrinks.
        delete next.page;
        return next;
      },
    },
  });
};

const FILTER_PARAM_KEYS = Object.values(RESOLVER_PARAM_BY_FIELD);

export const getClearFiltersAction = (
  nodeId: string,
): MasonryEngineNodeAction<'clearFilters', { nodeId: string }> => ({
  type: 'clearFilters',
  namespace: FILTER_NAMESPACE,
  payload: { nodeId },
});

// Clears every filter query param in a single reload (Clear All). Mirrors
// setFilterActionHandler's reload-with-setQueryParams mechanism.
export const clearFiltersActionHandler: MasonryEngineActionHandler<
  MasonryEngineNodeAction<'clearFilters', Record<string, any>>,
  MasonryDriverAction
> = ({ payload }, { messagingController }) => {
  messagingController.sendMessage(payload.nodeId as string, {
    type: 'reload',
    payload: {
      renderIntermediately: 'loader',
      setQueryParams: (current: Record<string, string>) => {
        const next = { ...current };
        FILTER_PARAM_KEYS.forEach((key) => delete next[key]);
        delete next.page;
        return next;
      },
    },
  });
};

type Option = {
  label: string;
  value?: string;
  disabled?: boolean;
  options?: { label: string; value: string; disabled?: boolean }[];
};

type FormFieldProperties = {
  formFieldId: string;
  label?: string;
  hideLabel?: boolean;
  hint?: string;
  showOptionalFlag?: boolean;
  inputStatus?: 'success' | 'warning' | 'error';
  statusMessage?: string;
};

type Props = {
  formFieldProperties: FormFieldProperties;
  value?: string;
  placeholder?: string;
  options: Option[];
  disabled?: boolean;
  locatorId?: string;
};

const FilterTrigger = styled(Popover.Trigger, {
  display: 'flex',
  flexDirection: 'row-reverse',
  alignItems: 'center',
  appearance: 'none',
  gap: '$half',
  minWidth: 'max-content',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  backgroundColor: '$inputBackgroundDefault !important',
  border: '$borderWidths$thin solid $inputBorderDefault !important',
  borderRadius: '$medium !important',
  typography: '$bodyTwo',
  color: '$onSurfaceTextPrimary !important',
  padding: '$threeQuarters $one !important',
  svg: { color: '$onSurfaceIconPrimary !important' },
  '&:hover': { backgroundColor: '$inputBackgroundHovered !important' },
  '&[aria-expanded="true"]': {
    borderColor: '$interactiveActionPrimary !important',
  },
});

const FilterPopoverContent = styled(Popover.Content, {
  zIndex: '$default',
  maxHeight: 'var(--radix-popover-content-available-height)',
  overflowY: 'auto',
});

export const ScfhpSelectInputRenderer = ({
  formFieldProperties,
  value,
  placeholder,
  options,
  disabled,
  locatorId,
}: Props) => {
  const { formFieldId, label } = formFieldProperties;

  const invokeAction = useMasonryEngineActionEmitter();

  const paramKey = RESOLVER_PARAM_BY_FIELD[formFieldId];
  const readParamValue = useCallback(
    () =>
      paramKey
        ? new URLSearchParams(window.location.search).get(paramKey) ?? ''
        : '',
    [paramKey],
  );
  const [selectedValue, setSelectedValue] = useState(readParamValue);
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    const sync = () => setSelectedValue(readParamValue());
    window.addEventListener(FILTERS_CHANGED_EVENT, sync);
    window.addEventListener('popstate', sync);
    return () => {
      window.removeEventListener(FILTERS_CHANGED_EVENT, sync);
      window.removeEventListener('popstate', sync);
    };
  }, [readParamValue]);
  const currentValue = selectedValue || value || '';

  const flatOptions = useMemo(
    () =>
      options.flatMap(
        (opt): { label: string; value?: string; disabled?: boolean }[] =>
          opt.options
            ? opt.options
            : [{ label: opt.label, value: opt.value, disabled: opt.disabled }],
      ),
    [options],
  );

  const triggerLabel = useMemo(() => {
    if (!currentValue) return placeholder || label;
    const match = flatOptions.find((opt) => (opt.value || '') === currentValue);
    return match ? match.label : placeholder || label;
  }, [currentValue, flatOptions, placeholder, label]);

  const applyFilter = useCallback(
    (raw: string) => {
      if (!paramKey) return;
      const paramValue = SENTINEL_VALUES_FOR_ALL.has(raw) ? '' : raw;
      replaceSearchParam(paramKey, paramValue);
      invokeAction(getSetFilterAction(RELOAD_NODE_ID, paramKey, paramValue));
      setIsOpen(false);
    },
    [paramKey, invokeAction],
  );

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <FilterTrigger
        label={triggerLabel}
        icon={isOpen ? 'interfaceChevronUp' : 'interfaceChevronDown'}
        disabled={disabled}
        aria-label={currentValue ? `${label}: ${triggerLabel}` : label}
        data-testid={locatorId}
      />
      <GDSPopoverPrimitive.Portal>
        <FilterPopoverContent
          side="bottom"
          align="start"
          sideOffset={8}
          avoidCollisions={false}
          showCloseButton={false}
        >
          <Fieldset hideLegend legend={label}>
            <StackLayout spacing="$half" css={{ alignItems: 'baseline' }}>
              {flatOptions.map((opt) => {
                const optionValue = opt.value || 'ALL';
                return (
                  <Radio
                    key={`${formFieldId}-${optionValue}`}
                    id={`${formFieldId}-${optionValue}`}
                    label={opt.label}
                    name={formFieldId}
                    type="radio"
                    value={optionValue}
                    disabled={opt.disabled}
                    checked={(opt.value || '') === currentValue}
                    onChange={() => applyFilter(optionValue)}
                  />
                );
              })}
            </StackLayout>
          </Fieldset>
        </FilterPopoverContent>
      </GDSPopoverPrimitive.Portal>
    </Popover.Root>
  );
};
